/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the patient summary component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * React
 */
import * as React from 'react';

import { makeStyles, Theme, useTheme } from '@material-ui/core/styles';

import { faUser } from '@fortawesome/pro-duotone-svg-icons/faUser';

import { faHeartbeat } from '@fortawesome/pro-duotone-svg-icons/faHeartbeat';

import { faBuilding } from '@fortawesome/pro-duotone-svg-icons/faBuilding';

import Grid from '@material-ui/core/Grid';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

import * as Dtos from '../../api/Neon.Dtos';
import InformationCard from '../InformationCard/InformationCard';
import { PatientContext, InstitutionContext, IPatient } from '@ngt/online-patient-management-ui';
import { usePatientDrugs } from '../../store/modules/drugmanagement/patientdrugs';
import { usePharmacy } from '../../store/modules/drugmanagement/pharmacy';


/*
 * ---------------------------------------------------------------------------------
 * interfaces
 * ---------------------------------------------------------------------------------
 */

interface IPatientDrugInformationProps {
    patient?: IPatient | null;
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles<Theme>(theme => ({
    item: {
        display: 'flex',

        '& > *': {
            flex: '1 1 auto'
        }
    }
}));

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */


const PatientDrugInformation: React.FunctionComponent<IPatientDrugInformationProps> = ({
    patient
}) => {

    const theme = useTheme()
    const classes = useStyles();
    const [contextPatient] = React.useContext(PatientContext);
    const [institution] = React.useContext(InstitutionContext);

    const typedPatient: Dtos.Patient | null = (patient ?? contextPatient) as any;

    const [patientDrugs, , , ] = usePatientDrugs();

    const [pharmacy, , , ] = usePharmacy();

    const status = React.useMemo(() => {
        return <>
            {
                pharmacy?.pharmacyDrugs?.map((pd) => {
                    return <div><small>{pd.drug.drugName} &mdash; {patientDrugs?.find(x => x.drugId === pd.drugId && x.patientTreatmentStatus === Dtos.PatientTreatmentStatus.OnTreatment) ? 'On Treatment' : 'Off Treatment'}</small></div>
                })
            }
        </>
    }, [pharmacy, patientDrugs]);

    return (
        <Grid
            container
            spacing={3}
        >
            <Grid
                item
                xs={12}
                md={4}
                className={classes.item}
            >
                <InformationCard
                    color={theme.palette.primary.main}
                    icon={faUser}
                    title="Patient"
                    text={typedPatient?.studyNumber ?? 'New Patient'}
                    caption={typedPatient?.treatmentId === 1 ? 'Arm A' : typedPatient?.treatmentId === 2 ? 'Arm B' : typedPatient?.treatmentId === 3 ? 'Arm C' : ''}
                />
            </Grid>
            <Grid
                item
                xs={12}
                md={4}
                className={classes.item}
            >
                <InformationCard
                    color={theme.palette.secondary.main}
                    icon={faBuilding}
                    title="Institution"
                    text={institution?.code}
                    caption={institution?.name}
                />
            </Grid>
            <Grid
                item
                xs={12}
                md={4}
                className={classes.item}
            >
                <InformationCard
                    color="#76c6d7"
                    icon={faHeartbeat}
                    title="Status"
                    text={status}
                />
            </Grid>
        </Grid>
    );
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default PatientDrugInformation;
